import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { unwrapList, apiErrorMessage } from '@/lib/api-helpers';
import { Button } from '@ouropix/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Input } from '@ouropix/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ouropix/ui';
import { ConfirmModal } from '@ouropix/ui';
import {
  Eye, DollarSign, TrendingUp, Award,
  Play, Trophy, CheckCircle,
  ChevronLeft, ChevronRight, Download, Search,
} from 'lucide-react';
import AdminPageHeader from '@/components/layout/PageHeader';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { GameDetailModal } from '@/components/games/GameDetailModal';
import type { AdminGame } from '@/components/games/types';

const PAGE_SIZE = 20;

export const Route = createFileRoute('/_admin/jogos/encerrados')({
  component: JogosEncerradosPage,
});

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function JogosEncerradosPage() {
  const queryClient = useQueryClient();
  const [gameToView, setGameToView] = useState<AdminGame | null>(null);
  const [defaultViewTab, setDefaultViewTab] = useState('info');
  const [gameToReactivate, setGameToReactivate] = useState<AdminGame | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const { data: games, isLoading } = useQuery<AdminGame[]>({
    queryKey: ['admin-games-completed'],
    queryFn: async () => {
      const r = await api.get('/admin/games/completed', { params: { limit: 500 } });
      const raw: any[] = unwrapList(r.data, 'games');
      return raw.map((g: any) => ({
        ...g,
        modality: g.modality ? (typeof g.modality === 'string' ? { id: '', name: g.modality, code: '', icon: null } : g.modality) : null,
        has_final_draw: g.hasFinalDraw ?? g.has_final_draw ?? false,
        draw_status: g.drawStatus ?? g.draw_status ?? null,
        end_at: g.end_at ?? g.endedAt ?? null,
        start_at: g.start_at ?? null,
        revenue_collected: g.revenue_collected ?? 0,
        prizes_paid: g.prizes_paid ?? 0,
        used_plays: g.used_plays ?? g.soldPool ?? 0,
        total_plays: g.total_plays ?? g.totalPool ?? 0,
        availableInPool: g.available_in_pool ?? g.availableInPool ?? null,
        icon_url: g.icon_url ?? null,
        theme: g.theme ?? null,
      })) as AdminGame[];
    },
  });

  const reactivateGameMutation = useMutation({
    mutationFn: async (gameId: string) => {
      await api.post(`/admin/games/${gameId}/reactivate`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-games-completed'] });
      queryClient.invalidateQueries({ queryKey: ['admin-games-active'] });
      toast.success('Jogo reativado com sucesso!');
      setGameToReactivate(null);
    },
    onError: (error: unknown) => {
      toast.error(apiErrorMessage(error, 'Erro ao reativar jogo'));
    },
  });

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const r = await api.get('/admin/financial/revenue/export', {
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `jogos-encerrados-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exportado!');
    } catch {
      toast.error('Erro ao exportar CSV');
    } finally {
      setIsExporting(false);
    }
  };

  const allGames = games ?? [];
  const filtered = search
    ? allGames.filter(g => g.title.toLowerCase().includes(search.toLowerCase()))
    : allGames;

  const totalRevenue = allGames.reduce((sum, g) => sum + (g.revenue_collected || 0), 0);
  const totalPrizesPaid = allGames.reduce((sum, g) => sum + (g.prizes_paid || 0), 0);
  const totalProfit = totalRevenue - totalPrizesPaid;
  const avgProfitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginatedGames = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={CheckCircle}
        title="Jogos Encerrados"
        description="Histórico de jogos finalizados e relatórios de resultados"
        actions={
          <Button variant="outline" onClick={handleExportCSV} disabled={isExporting}>
            <Download className="mr-2 h-4 w-4" />
            {isExporting ? 'Exportando...' : 'Exportar CSV'}
          </Button>
        }
      />

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Encerrados</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{allGames.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <DollarSign className="h-4 w-4" />Receita Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{formatCurrency(totalRevenue)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Award className="h-4 w-4" />Prêmios Pagos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-500">{formatCurrency(totalPrizesPaid)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <TrendingUp className="h-4 w-4" />Lucro Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${totalProfit >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {formatCurrency(totalProfit)}
            </div>
            <p className="text-xs text-muted-foreground">Margem: {avgProfitMargin.toFixed(1)}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtro de busca */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nome..."
          value={search}
          onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
          className="pl-9"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="min-w-[900px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Jogo</TableHead>
                  <TableHead>Modalidade</TableHead>
                  <TableHead>Jogadas</TableHead>
                  <TableHead>Disponíveis</TableHead>
                  <TableHead>Receita</TableHead>
                  <TableHead>Prêmios</TableHead>
                  <TableHead>Lucro</TableHead>
                  <TableHead>Margem</TableHead>
                  <TableHead>Período</TableHead>
                  <TableHead>Sorteio</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8">Carregando...</TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                      {search ? 'Nenhum jogo encontrado para a busca.' : 'Nenhum jogo encerrado ainda.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedGames.map(game => {
                    const revenue = game.revenue_collected || 0;
                    const prizes = game.prizes_paid || 0;
                    const profit = revenue - prizes;
                    const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
                    const availablePlays = game.availableInPool ?? (game.total_plays - (game.used_plays || 0));
                    const canReactivate = availablePlays > 0;

                    return (
                      <TableRow key={game.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {game.icon_url && (
                              <img src={game.icon_url} alt={`Ícone ${game.title}`} className="h-10 w-10 rounded-lg object-cover" loading="lazy" />
                            )}
                            <div>
                              <p className="font-medium">{game.title}</p>
                              <p className="text-xs text-muted-foreground">{game.theme?.name || '-'}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{game.modality?.icon} {game.modality?.name || '-'}</Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">
                            {(game.used_plays || 0).toLocaleString('pt-BR')}/{(game.total_plays || 0).toLocaleString('pt-BR')}
                          </span>
                        </TableCell>
                        <TableCell>
                          {canReactivate ? (
                            <Badge variant="secondary" className="bg-blue-500/10 text-blue-500">
                              {availablePlays.toLocaleString('pt-BR')} restantes
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground">
                              Esgotado
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-green-500 font-medium">{formatCurrency(revenue)}</TableCell>
                        <TableCell className="text-amber-500">{formatCurrency(prizes)}</TableCell>
                        <TableCell className={profit >= 0 ? 'text-green-500 font-medium' : 'text-red-500 font-medium'}>
                          {formatCurrency(profit)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={margin >= 20 ? 'default' : margin >= 0 ? 'secondary' : 'destructive'}>
                            {margin.toFixed(1)}%
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs">
                            <p>Inicio: {game.start_at ? format(new Date(game.start_at), 'dd/MM/yyyy', { locale: ptBR }) : '-'}</p>
                            <p className="text-muted-foreground">
                              Fim: {game.end_at ? format(new Date(game.end_at), 'dd/MM/yyyy', { locale: ptBR }) : '-'}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {game.has_final_draw ? (
                            game.draw_status === 'completed' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setDefaultViewTab('draw'); setGameToView(game); }}
                                className="text-green-500 hover:text-green-600 hover:bg-green-500/10 gap-1"
                              >
                                <CheckCircle className="h-4 w-4" />
                                Ver Resultado
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setDefaultViewTab('draw'); setGameToView(game); }}
                                className="text-purple-500 hover:text-purple-600 hover:bg-purple-500/10 gap-1"
                              >
                                <Trophy className="h-4 w-4" />
                                Sortear
                              </Button>
                            )
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {canReactivate && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setGameToReactivate(game)}
                                title="Reativar jogo"
                                aria-label="Reativar jogo"
                                className="text-blue-500 hover:text-blue-600 hover:bg-blue-500/10"
                              >
                                <Play className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setDefaultViewTab('info'); setGameToView(game); }}
                              aria-label="Ver detalhes"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-xs text-muted-foreground">
                Mostrando {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} de {filtered.length}
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)} aria-label="Página anterior">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs px-2 text-muted-foreground">{currentPage} / {totalPages}</span>
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} aria-label="Próxima página">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {gameToView && (
        <GameDetailModal
          open={!!gameToView}
          onOpenChange={() => setGameToView(null)}
          game={gameToView}
          allowDrawExecution
          defaultTab={defaultViewTab}
        />
      )}

      <ConfirmModal
        isOpen={!!gameToReactivate}
        onClose={() => setGameToReactivate(null)}
        onConfirm={() => { if (gameToReactivate) reactivateGameMutation.mutate(gameToReactivate.id); }}
        title="Reativar Sorteio"
        description={
          <div className="space-y-2">
            <p>Deseja reativar o sorteio <strong>"{gameToReactivate?.title}"</strong>?</p>
            <p className="text-sm text-muted-foreground">
              O jogo voltará a aparecer no site e os usuários poderão comprar os números restantes.
            </p>
          </div>
        }
        confirmText={reactivateGameMutation.isPending ? 'Reativando...' : 'Reativar'}
        cancelText="Cancelar"
      />
    </div>
  );
}
